/**
 * A very small Markdown reader for the legal pages — /privacy and /terms.
 *
 * WHY NOT A LIBRARY. The two documents it reads are ours (`PRIVACY-POLICY.md` and
 * `TERMS-OF-SERVICE.md` in the repository root), they use a dozen constructs, and they
 * change a few times a year. A markdown dependency would mean a `yarn add`, a lockfile
 * change and a Docker rebuild for that, and it would happily render raw HTML — which a
 * legal page must never do. This reads exactly what those documents use and nothing else:
 *
 *   #, ##, ###     headings          - item / 1. item    lists (wrapped lines allowed)
 *   | a | b |      tables            > quoted             blockquotes
 *   ---            rules             **bold** *italic* `code` [text](url)
 *   privacy@…      email addresses become mailto links
 *   >>> … <<<      REVIEW MARKERS — text the drafter could not source. Kept as a
 *                  distinct token so the page can refuse to publish while any remain.
 *
 * It returns plain data (blocks of inline tokens), not React, so it can be tested with
 * `node src/lib/legalMarkdown.checks.mjs` and so the same output can be rendered as HTML
 * for a mock-up. Rendering lives in components/LegalDocument.jsx.
 */

// A `>>>` directly after a backtick is being quoted (as in the draft banner), not opened.
const MARKER_RE = /(?<!`)>>>([\s\S]*?)<<</g;
const EMAIL_RE = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;

/** Does this text still carry anything a human has to resolve before it goes live? */
export function hasReviewMarkers(text) {
  return />>>/.test(text) || /<<</.test(text) || /DRAFT — NOT YET IN FORCE/.test(text);
}

export function countReviewMarkers(text) {
  return (text.match(/>>>/g) || []).length;
}

// ---------------------------------------------------------------------------
// Inline parsing: text → [{type:'text'|'bold'|'italic'|'code'|'link'|'marker', …}]
// ---------------------------------------------------------------------------

function parseEmphasis(text) {
  // Handles **bold**, *italic*, `code`, [text](url) and bare emails, left to right.
  const out = [];
  let i = 0;
  const pushText = (s) => {
    if (!s) return;
    // Bare email addresses → mailto links.
    let last = 0;
    for (const m of s.matchAll(EMAIL_RE)) {
      if (m.index > last) out.push({ type: 'text', text: s.slice(last, m.index) });
      out.push({ type: 'link', href: `mailto:${m[0]}`, text: m[0] });
      last = m.index + m[0].length;
    }
    if (last < s.length) out.push({ type: 'text', text: s.slice(last) });
  };
  let buf = '';
  while (i < text.length) {
    if (text.startsWith('**', i)) {
      const end = text.indexOf('**', i + 2);
      if (end > i + 2) {
        pushText(buf); buf = '';
        out.push({ type: 'bold', children: parseEmphasis(text.slice(i + 2, end)) });
        i = end + 2; continue;
      }
    }
    if (text[i] === '`') {
      const end = text.indexOf('`', i + 1);
      if (end > i + 1) {
        pushText(buf); buf = '';
        out.push({ type: 'code', text: text.slice(i + 1, end) });
        i = end + 1; continue;
      }
    }
    if (text[i] === '[') {
      const close = text.indexOf('](', i + 1);
      const end = close > 0 ? text.indexOf(')', close + 2) : -1;
      if (close > i + 1 && end > close) {
        pushText(buf); buf = '';
        out.push({ type: 'link', href: text.slice(close + 2, end), text: text.slice(i + 1, close) });
        i = end + 1; continue;
      }
    }
    if (text[i] === '*' && text[i + 1] !== '*' && text[i + 1] !== ' ') {
      const end = text.indexOf('*', i + 1);
      if (end > i + 1 && text[end - 1] !== ' ') {
        pushText(buf); buf = '';
        out.push({ type: 'italic', children: parseEmphasis(text.slice(i + 1, end)) });
        i = end + 1; continue;
      }
    }
    buf += text[i];
    i += 1;
  }
  pushText(buf);
  return out;
}

function parseMarkers(text) {
  // Review markers can wrap anything — bold, links, whole sentences — so they are split
  // out first and their contents parsed like any other text.
  const out = [];
  let last = 0;
  for (const m of text.matchAll(MARKER_RE)) {
    if (m.index > last) out.push(...parseEmphasis(text.slice(last, m.index)));
    out.push({ type: 'marker', children: parseEmphasis(m[1].trim()) });
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(...parseEmphasis(text.slice(last)));
  return out;
}

export function parseInline(text) {
  return parseMarkers(text);
}

// ---------------------------------------------------------------------------
// Block parsing: text → [{type:'heading'|'paragraph'|'list'|'table'|'quote'|'rule', …}]
// ---------------------------------------------------------------------------

const isBlank = (l) => l.trim() === '';
const isRule = (l) => /^\s*-{3,}\s*$/.test(l);
const headingOf = (l) => /^(#{1,3})\s+(.*)$/.exec(l);
const bulletOf = (l) => /^(\s*)[-*]\s+(.*)$/.exec(l);
const numberOf = (l) => /^(\s*)\d+\.\s+(.*)$/.exec(l);
const isTableRow = (l) => /^\s*\|.*\|\s*$/.test(l);
const isTableSep = (l) => /^\s*\|(\s*:?-+:?\s*\|)+\s*$/.test(l);
// A single ">" opens a quote. ">>>" opens a review marker, which is inline text, not a quote.
const isQuote = (l) => /^\s*>(?!>)/.test(l);

function splitCells(line) {
  const inner = line.trim().replace(/^\|/, '').replace(/\|$/, '');
  return inner.split('|').map((c) => c.trim());
}

/** Join wrapped lines of one paragraph or list item into a single string. */
const joinLines = (lines) => lines.map((l) => l.trim()).join(' ');

export function parseMarkdown(text) {
  const lines = text.replace(/\r\n?/g, '\n').split('\n');
  const blocks = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (isBlank(line)) { i += 1; continue; }

    if (isRule(line)) { blocks.push({ type: 'rule' }); i += 1; continue; }

    const h = headingOf(line);
    if (h) {
      blocks.push({ type: 'heading', level: h[1].length, children: parseInline(h[2].trim()) });
      i += 1; continue;
    }

    if (isQuote(line)) {
      const inner = [];
      while (i < lines.length && isQuote(lines[i])) {
        inner.push(lines[i].replace(/^\s*>\s?/, ''));
        i += 1;
      }
      blocks.push({ type: 'quote', children: parseMarkdown(inner.join('\n')) });
      continue;
    }

    if (isTableRow(line) && i + 1 < lines.length && isTableSep(lines[i + 1])) {
      const header = splitCells(line).map(parseInline);
      i += 2;
      const rows = [];
      while (i < lines.length && isTableRow(lines[i])) {
        rows.push(splitCells(lines[i]).map(parseInline));
        i += 1;
      }
      blocks.push({ type: 'table', header, rows });
      continue;
    }

    const b = bulletOf(line) || numberOf(line);
    if (b) {
      const ordered = !!numberOf(line);
      const items = [];
      const test = ordered ? numberOf : bulletOf;
      while (i < lines.length) {
        const m = test(lines[i]);
        if (!m) break;
        const item = [m[2]];
        i += 1;
        // Continuation lines are indented and not themselves items or blank.
        while (i < lines.length && !isBlank(lines[i]) && /^\s{2,}/.test(lines[i]) && !test(lines[i])) {
          item.push(lines[i]);
          i += 1;
        }
        items.push(parseInline(joinLines(item)));
        // A blank line between items is allowed only if the next line is another item.
        if (i < lines.length && isBlank(lines[i]) && i + 1 < lines.length && test(lines[i + 1])) i += 1;
      }
      blocks.push({ type: 'list', ordered, items });
      continue;
    }

    // Paragraph: everything up to a blank line or the start of another block.
    const para = [];
    while (i < lines.length && !isBlank(lines[i]) && !isRule(lines[i]) && !headingOf(lines[i])
           && !isQuote(lines[i]) && !bulletOf(lines[i]) && !numberOf(lines[i])
           && !(isTableRow(lines[i]) && i + 1 < lines.length && isTableSep(lines[i + 1]))) {
      para.push(lines[i]);
      i += 1;
    }
    if (para.length) blocks.push({ type: 'paragraph', children: parseInline(joinLines(para)) });
    else i += 1; // defensive: never loop forever on a line nothing claims
  }
  return blocks;
}

/**
 * Pull the H1 and the `*Last updated: …*` line out of the document, so the page can draw
 * its own title block; `body` is what remains. `updated` is inline tokens (it may hold a
 * review marker until the date is set), or null when the document has no such line.
 */
export function splitDocument(text) {
  let body = text.replace(/\r\n?/g, '\n');
  let title = null;
  const h = /^#\s+(.+)$/m.exec(body);
  if (h) { title = h[1].trim(); body = body.replace(h[0], ''); }
  let updated = null;
  const u = /^\*Last updated:\s*(.+?)\*\s*$/m.exec(body);
  if (u) { updated = parseInline(u[1].trim()); body = body.replace(u[0], ''); }
  return { title, updated, body };
}
